/**
 * 主画布组件
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useDebouncedCallback } from 'use-debounce';
import '@xyflow/react/dist/style.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

import { nodeTypes } from './nodes';
import { Toolbar } from './Toolbar';
import { NodeOperationsProvider, useNodeOperations } from './providers/NodeOperationsProvider';
import { NodeDropzoneProvider } from './providers/NodeDropzoneProvider';
import { ConnectionGuideEdge } from './edges/ConnectionGuideEdge';
import {
  buildConnectionGuideEdges,
  CONNECTION_GUIDE_EDGE_TYPE,
} from './edges/connectionGuide';
import './Canvas.css';

const edgeTypes = {
  [CONNECTION_GUIDE_EDGE_TYPE]: ConnectionGuideEdge,
};

const CanvasInner = ({
  projectId,
  initialNodes = [],
  initialEdges = [],
  onSave,
  readOnly = false,
}) => {
  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const [panMode, setPanMode] = useState(true); // true: 抓取模式, false: 选择模式
  const { screenToFlowPosition } = useReactFlow();
  const connectingNodeId = useRef(null);
  const isInitialMount = useRef(true);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | null
  const isMountedRef = useRef(true); // 追踪组件是否已挂载
  const saveStatusTimerRef = useRef(null); // 保存状态定时器引用
  const displayEdges = useMemo(
    () => buildConnectionGuideEdges(edges, nodes),
    [edges, nodes]
  );

  // 组件卸载时的清理
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // 清理保存状态定时器
      if (saveStatusTimerRef.current) {
        clearTimeout(saveStatusTimerRef.current);
      }
    };
  }, []);

  // 自动保存（防抖 2 秒）
  const debouncedSave = useDebouncedCallback(async () => {
    // 只读模式下不保存
    if (readOnly || !onSave || isInitialMount.current) {
      return;
    }
    
    try {
      if (!isMountedRef.current) return; // 检查组件是否已挂载
      setSaveStatus('saving');
      await onSave(nodes, edges);
      if (!isMountedRef.current) return; // 再次检查
      setSaveStatus('saved');
      console.log('Canvas 自动保存成功');
      
      // 清除之前的定时器
      if (saveStatusTimerRef.current) {
        clearTimeout(saveStatusTimerRef.current);
      }
      
      // 2秒后清除"已保存"状态
      saveStatusTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setSaveStatus(null);
        }
      }, 2000);
    } catch (err) {
      console.error('Canvas 自动保存失败:', err);
      if (isMountedRef.current) {
        setSaveStatus(null);
      }
    }
  }, 2000); // 改为2秒防抖，避免过于频繁的保存

  // 当 nodes 或 edges 改变时触发自动保存
  useEffect(() => {
    // 跳过初始加载时的保存
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // 只读模式下不保存
    if (readOnly) {
      return;
    }
    
    // 触发自动保存（包括空画布的情况）
    console.log('Canvas 检测到变化，准备自动保存');
    debouncedSave();
    
    // 清理函数：取消防抖
    return () => {
      debouncedSave.cancel();
    };
  }, [nodes, edges, debouncedSave, readOnly]);

  // 节点变化处理
  const onNodesChange = useCallback(
    (changes) => {
      // 只读模式下，只允许 'select' 和 'dimensions' 类型的变化，其他操作都禁止
      if (readOnly) {
        const allowedChanges = changes.filter(
          (change) => change.type === 'select' || change.type === 'dimensions'
        );
        setNodes((nds) => applyNodeChanges(allowedChanges, nds));
      } else {
        setNodes((nds) => applyNodeChanges(changes, nds));
      }
    },
    [readOnly]
  );

  // 连线变化处理
  const onEdgesChange = useCallback(
    (changes) => {
      // 只读模式下，只允许 'select' 类型的变化，其他操作都禁止
      if (readOnly) {
        const allowedChanges = changes.filter((change) => change.type === 'select');
        setEdges((eds) => applyEdgeChanges(allowedChanges, eds));
      } else {
        setEdges((eds) => applyEdgeChanges(changes, eds));
      }
    },
    [readOnly]
  );

  // 连接验证 - 定义允许的连接规则
  const isValidConnection = useCallback((connection) => {
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    
    if (!sourceNode || !targetNode) return false;

    // 新版图片模型共用统一输入契约。
    if (['gptImage2', 'nanoBanana2'].includes(targetNode.type) && connection.targetHandle) {
      if (connection.targetHandle === 'prompt') {
        return sourceNode.type === 'promptInput';
      }
      if (connection.targetHandle === 'image') {
        return ['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode.type);
      }
    }

    if (['minimaxH3Video', 'klingO3ReferenceVideo', 'geminiOmniFlashVideo', 'superSeed2LiteVideo'].includes(targetNode.type) && connection.targetHandle) {
      if (connection.targetHandle === 'prompt') {
        return sourceNode.type === 'promptInput';
      }
      if (connection.targetHandle === 'image') {
        return ['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode.type);
      }
      if (connection.targetHandle === 'video') {
        return ['minimaxH3Video', 'klingO3ReferenceVideo', 'superSeed2LiteVideo'].includes(targetNode.type)
          && ['videoReference', 'videoTask'].includes(sourceNode.type);
      }
      if (connection.targetHandle === 'audio') {
        return targetNode.type === 'superSeed2LiteVideo'
          && ['audioReference', 'audioTask', 'audioTrim', 'textToAudio', 'hailuoMusic', 'sunoMusic'].includes(sourceNode.type);
      }
    }

    // 特殊处理：可灵对口型节点的不同输入handle
    if (targetNode.type === 'klingLipSync' && connection.targetHandle) {
      if (connection.targetHandle === 'video') {
        // video handle 接受 videoTask 和 videoReference 节点
        return sourceNode.type === 'videoTask' || sourceNode.type === 'videoReference';
      } else if (connection.targetHandle === 'audio') {
        // audio handle 接受 audioReference 和 audioTask 节点
        return sourceNode.type === 'audioReference' || sourceNode.type === 'audioTask';
      } else if (connection.targetHandle === 'text') {
        // text handle 只接受 promptInput 节点
        return sourceNode.type === 'promptInput';
      }
    }

    // 特殊处理：音视频合并节点的不同输入handle
    if (targetNode.type === 'videoAudioMerge' && connection.targetHandle) {
      if (connection.targetHandle === 'video') {
        // video handle 接受 videoTask 和 videoReference 节点
        return sourceNode.type === 'videoTask' || sourceNode.type === 'videoReference';
      } else if (connection.targetHandle === 'audio') {
        // audio handle 接受 audioReference、audioTask 和 textToAudio 节点
        return ['audioReference', 'audioTask', 'textToAudio', 'hailuoMusic'].includes(sourceNode.type);
      }
    }

    // 特殊处理：Google Banana 节点的不同输入handle
    if (targetNode.type === 'googleBanana' && connection.targetHandle) {
      if (connection.targetHandle === 'prompt') {
        // prompt handle 只接受 promptInput 节点
        return sourceNode.type === 'promptInput';
      } else if (connection.targetHandle === 'image') {
        // image handle 接受图片相关节点（图生图模式）
        return ['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode.type);
      }
    }

    // 特殊处理：Banana Pro 节点的不同输入handle
    if (targetNode.type === 'bananaPro' && connection.targetHandle) {
      if (connection.targetHandle === 'prompt') {
        // prompt handle 只接受 promptInput 节点
        return sourceNode.type === 'promptInput';
      } else if (connection.targetHandle === 'image') {
        // image handle 接受图片相关节点（图生图模式，最多14张）
        return ['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode.type);
      }
    }

    // 特殊处理：Minimax 画图节点的不同输入handle
    if (targetNode.type === 'minimaxImage' && connection.targetHandle) {
      if (connection.targetHandle === 'prompt') {
        // prompt handle 只接受 promptInput 节点
        return sourceNode.type === 'promptInput';
      } else if (connection.targetHandle === 'image') {
        // image handle 接受图片相关节点（图生图模式）
        return ['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode.type);
      }
    }

    // 特殊处理：即梦生图节点的不同输入handle
    if (targetNode.type === 'imengImageGen' && connection.targetHandle) {
      if (connection.targetHandle === 'prompt') {
        // prompt handle 只接受 promptInput 节点
        return sourceNode.type === 'promptInput';
      } else if (connection.targetHandle === 'image') {
        // image handle 接受图片相关节点（图生图模式）
        return ['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode.type);
      }
    }

    // 特殊处理：Sora角色创建节点的不同输入handle
    if (targetNode.type === 'soraRoleCreate' && connection.targetHandle) {
      if (connection.targetHandle === 'image') {
        // image handle 接受图片相关节点
        return ['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode.type);
      } else if (connection.targetHandle === 'video') {
        // video handle 接受视频任务节点和视频参考节点
        return ['videoTask', 'videoReference'].includes(sourceNode.type);
      }
    }

    // 定义连接规则
    const connectionRules = {
      // 文本输入节点可以连接到：文生图、图生图、视频生成、可灵对口型、语音合成、海螺画图、Google Banana、Banana Pro
      promptInput: ['gptImage2', 'nanoBanana2', 'minimaxH3Video', 'klingO3ReferenceVideo', 'geminiOmniFlashVideo', 'superSeed2LiteVideo', 'textToImage', 'referenceToImage', 'imengImageGen', 'minimaxImage', 'googleBanana', 'bananaPro', 'sora2Video', 'veo3Video', 'doubaoVideo', 'hailuo23Video', 'klingVideo', 'klingLipSync', 'textToAudio', 'hailuoMusic', 'sunoMusic'],
      // 图片参考节点只能连接到：图生图、即梦生图、视频生成、海螺画图、Google Banana、Banana Pro、Sora角色创建（不能连接到文生图）
      imageReference: ['gptImage2', 'nanoBanana2', 'minimaxH3Video', 'klingO3ReferenceVideo', 'geminiOmniFlashVideo', 'superSeed2LiteVideo', 'referenceToImage', 'imengImageGen', 'minimaxImage', 'googleBanana', 'bananaPro', 'sora2Video', 'veo3Video', 'doubaoVideo', 'hailuo23Video', 'klingVideo', 'videoFaceSwap', 'soraRoleCreate'],
      // 手绘节点可以连接到：图生图、即梦生图、视频生成、海螺画图、Google Banana、Banana Pro、Sora角色创建（作为参考图片）
      drawing: ['gptImage2', 'nanoBanana2', 'minimaxH3Video', 'klingO3ReferenceVideo', 'geminiOmniFlashVideo', 'superSeed2LiteVideo', 'referenceToImage', 'imengImageGen', 'minimaxImage', 'googleBanana', 'bananaPro', 'sora2Video', 'veo3Video', 'doubaoVideo', 'hailuo23Video', 'klingVideo', 'videoFaceSwap', 'soraRoleCreate'],
      // 文生图节点可以连接到：任务节点
      textToImage: ['imageTask'],
      // 图生图节点可以连接到：任务节点
      referenceToImage: ['imageTask'],
      // 即梦生图节点可以连接到：任务节点
      imengImageGen: ['imageTask'],
      // 海螺画图节点可以连接到：任务节点
      minimaxImage: ['imageTask'],
      // Google Banana 节点可以连接到：任务节点
      googleBanana: ['imageTask'],
      // Banana Pro 节点可以连接到：任务节点
      bananaPro: ['imageTask'],
      gptImage2: ['imageTask'],
      nanoBanana2: ['imageTask'],
      minimaxH3Video: ['videoTask'],
      klingO3ReferenceVideo: ['videoTask'],
      geminiOmniFlashVideo: ['videoTask'],
      superSeed2LiteVideo: ['videoTask'],
      // 图片展示节点可以连接到其他显示节点、海螺画图、Google Banana、Banana Pro、即梦生图、Sora角色创建
      imageDisplay: ['imageDisplay', 'gptImage2', 'nanoBanana2', 'minimaxH3Video', 'klingO3ReferenceVideo', 'geminiOmniFlashVideo', 'superSeed2LiteVideo', 'minimaxImage', 'googleBanana', 'bananaPro', 'imengImageGen', 'klingVideo', 'videoFaceSwap', 'soraRoleCreate'],
      // 图片任务节点可以输出到图生图、即梦生图、海螺画图、Google Banana、Banana Pro 和视频生成（作为参考图片）、Sora角色创建
      imageTask: ['gptImage2', 'nanoBanana2', 'minimaxH3Video', 'klingO3ReferenceVideo', 'geminiOmniFlashVideo', 'superSeed2LiteVideo', 'referenceToImage', 'imengImageGen', 'minimaxImage', 'googleBanana', 'bananaPro', 'sora2Video', 'veo3Video', 'doubaoVideo', 'hailuo23Video', 'klingVideo', 'videoFaceSwap', 'soraRoleCreate'],
      // 视频参考节点可以连接到：视频换脸、可灵对口型、音视频合并、Sora角色创建
      videoReference: ['minimaxH3Video', 'klingO3ReferenceVideo', 'superSeed2LiteVideo', 'videoFaceSwap', 'klingLipSync', 'videoAudioMerge', 'soraRoleCreate'],
      // SORA2视频节点可以连接到：视频任务节点
      sora2Video: ['videoTask'],
      // VEO3视频节点可以连接到：视频任务节点
      veo3Video: ['videoTask'],
      // 豆包视频节点可以连接到：视频任务节点
      doubaoVideo: ['videoTask'],
      // 海螺2.3视频节点可以连接到：视频任务节点
      hailuo23Video: ['videoTask'],
      // 可灵2.5视频节点可以连接到：视频任务节点
      klingVideo: ['videoTask'],
      // 视频任务节点可以输出到视频生成（作为参考）、可灵对口型、音视频合并、Sora角色创建
      videoTask: ['minimaxH3Video', 'klingO3ReferenceVideo', 'superSeed2LiteVideo', 'sora2Video', 'veo3Video', 'doubaoVideo', 'hailuo23Video', 'klingVideo', 'videoFaceSwap', 'klingLipSync', 'videoAudioMerge', 'soraRoleCreate'],
      // 可灵对口型节点可以连接到：视频任务节点
      klingLipSync: ['videoTask'],
      // 音视频合并节点可以连接到：视频任务节点
      videoAudioMerge: ['videoTask'],
      // 语音合成节点可以连接到：音频任务节点、音视频合并节点、音频剪辑节点
      textToAudio: ['audioTask', 'superSeed2LiteVideo', 'videoAudioMerge', 'audioTrim'],
      // 音乐生成节点可以连接到：音频任务节点、音视频合并节点、音频剪辑节点
      hailuoMusic: ['audioTask', 'superSeed2LiteVideo', 'videoAudioMerge', 'audioTrim'],
      // Suno音乐生成节点可以连接到：音频任务节点、音视频合并节点、音频剪辑节点
      sunoMusic: ['audioTask', 'superSeed2LiteVideo', 'videoAudioMerge', 'audioTrim'],
      // 音频任务节点可以输出到音频参考节点、可灵对口型、音视频合并、音频剪辑
      audioTask: ['audioReference', 'superSeed2LiteVideo', 'klingLipSync', 'videoAudioMerge', 'audioTrim'],
      // 音频参考节点可以输出到可灵对口型、音视频合并、音频剪辑
      audioReference: ['superSeed2LiteVideo', 'klingLipSync', 'videoAudioMerge', 'audioTrim'],
      // 音频剪辑节点可以输出到音频任务节点
      audioTrim: ['audioTask', 'superSeed2LiteVideo'],
    };

    const allowedTargets = connectionRules[sourceNode.type] || [];
    return allowedTargets.includes(targetNode.type);
  }, [nodes]);

  // 创建连接
  const onConnect = useCallback(
    (connection) => {
      // 只读模式下禁止创建连接
      if (readOnly) {
        return;
      }
      
      const newEdge = {
        ...connection,
        id: nanoid(),
        type: 'default',
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [readOnly]
  );

  // 双击画布创建节点
  const handleDoubleClick = useCallback(
    (event) => {
      // 只读模式下禁止创建节点
      if (readOnly) {
        return;
      }
      
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // 默认创建文本输入节点
      const newNode = {
        id: nanoid(),
        type: 'promptInput',
        position,
        data: { prompt: '' },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [screenToFlowPosition, readOnly]
  );

  // 处理拖放 - 允许拖放
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // 处理放置节点
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      // 只读模式下禁止拖放节点
      if (readOnly) {
        return;
      }

      const nodeType = event.dataTransfer.getData('application/reactflow');
      
      // 检查是否是有效的节点类型
      if (!nodeType) {
        return;
      }

      // 计算放置位置（鼠标位置转换为画布坐标）
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // 新版图片模型统一创建提示词和可选参考图输入节点。
      if (nodeType === 'gptImage2' || nodeType === 'nanoBanana2') {
        const promptNodeId = nanoid();
        const imageRefNodeId = nanoid();
        const modelNodeId = nanoid();
        const promptNode = {
          id: promptNodeId,
          type: 'promptInput',
          position: { x: position.x - 450, y: position.y - 110 },
          data: { prompt: '' },
        };
        const imageRefNode = {
          id: imageRefNodeId,
          type: 'imageReference',
          position: { x: position.x - 450, y: position.y + 150 },
          data: { imageUrl: '' },
        };
        const modelNode = {
          id: modelNodeId,
          type: nodeType,
          position,
          data: {},
        };
        const promptEdge = {
          id: nanoid(),
          source: promptNodeId,
          target: modelNodeId,
          targetHandle: 'prompt',
          type: 'default',
        };
        const imageEdge = {
          id: nanoid(),
          source: imageRefNodeId,
          target: modelNodeId,
          targetHandle: 'image',
          type: 'default',
        };

        setNodes(nds => [...nds, promptNode, imageRefNode, modelNode]);
        requestAnimationFrame(() => {
          setEdges(eds => addEdge(imageEdge, addEdge(promptEdge, eds)));
        });
      } else if (['minimaxH3Video', 'klingO3ReferenceVideo', 'geminiOmniFlashVideo', 'superSeed2LiteVideo'].includes(nodeType)) {
        const promptNodeId = nanoid();
        const imageRefNodeId = nanoid();
        const modelNodeId = nanoid();
        const promptNode = {
          id: promptNodeId,
          type: 'promptInput',
          position: { x: position.x - 460, y: position.y - 115 },
          data: { prompt: '' },
        };
        const imageRefNode = {
          id: imageRefNodeId,
          type: 'imageReference',
          position: { x: position.x - 460, y: position.y + 155 },
          data: { imageUrl: '' },
        };
        const modelNode = {
          id: modelNodeId,
          type: nodeType,
          position,
          data: {},
        };
        const promptEdge = {
          id: nanoid(),
          source: promptNodeId,
          target: modelNodeId,
          targetHandle: 'prompt',
          type: 'default',
        };
        const imageEdge = {
          id: nanoid(),
          source: imageRefNodeId,
          target: modelNodeId,
          targetHandle: 'image',
          type: 'default',
        };

        setNodes(nds => [...nds, promptNode, imageRefNode, modelNode]);
        requestAnimationFrame(() => {
          setEdges(eds => addEdge(imageEdge, addEdge(promptEdge, eds)));
        });
      // 如果是文生图节点，自动创建文本输入节点并连接
      } else if (nodeType === 'textToImage') {
        // 创建文本输入节点（在文生图节点左侧）
        const promptNodeId = nanoid();
        const promptNode = {
          id: promptNodeId,
          type: 'promptInput',
          position: {
            x: position.x - 450, // 在左侧450px处
            y: position.y,
          },
          data: { prompt: '' },
        };

        // 创建文生图节点
        const textToImageNodeId = nanoid();
        const textToImageNode = {
          id: textToImageNodeId,
          type: nodeType,
          position,
          data: {},
        };

        // 创建连接边
        const newEdge = {
          id: nanoid(),
          source: promptNodeId,
          target: textToImageNodeId,
          type: 'default',
        };

        console.log('拖放创建文生图节点和文本输入节点:', position);
        // 使用受控状态添加节点，避免与内部 store 的竞态
        setNodes((nds) => [...nds, promptNode, textToImageNode]);
        
        // 使用微队列延迟添加边，确保节点完全渲染后再添加边
        requestAnimationFrame(() => {
          setEdges((eds) => addEdge(newEdge, eds));
        });
      } else if (nodeType === 'referenceToImage') {
        // 如果是参考生图节点，自动创建文本输入节点和图片参考节点并连接
        // 创建文本输入节点（在参考生图节点左上方）
        const promptNodeId = nanoid();
        const promptNode = {
          id: promptNodeId,
          type: 'promptInput',
          position: {
            x: position.x - 450, // 在左侧450px处
            y: position.y - 100, // 在上方100px处
          },
          data: { prompt: '' },
        };

        // 创建图片参考节点（在参考生图节点左下方）
        const imageRefNodeId = nanoid();
        const imageRefNode = {
          id: imageRefNodeId,
          type: 'imageReference',
          position: {
            x: position.x - 450, // 在左侧450px处
            y: position.y + 150, // 在下方150px处
          },
          data: { imageUrl: '' },
        };

        // 创建参考生图节点
        const referenceToImageNodeId = nanoid();
        const referenceToImageNode = {
          id: referenceToImageNodeId,
          type: nodeType,
          position,
          data: {},
        };

        // 创建连接边：文本输入 -> 参考生图（连接到文本输入点）
        const promptEdge = {
          id: nanoid(),
          source: promptNodeId,
          target: referenceToImageNodeId,
          targetHandle: 'prompt',
          type: 'default',
        };

        // 创建连接边：图片参考 -> 参考生图（连接到图片输入点）
        const imageEdge = {
          id: nanoid(),
          source: imageRefNodeId,
          target: referenceToImageNodeId,
          targetHandle: 'image',
          type: 'default',
        };

        console.log('拖放创建参考生图节点及相关节点:', position);
        // 使用受控状态添加节点，避免与内部 store 的竞态
        setNodes((nds) => [...nds, promptNode, imageRefNode, referenceToImageNode]);
        
        // 使用微队列延迟添加边，确保节点完全渲染后再添加边
        requestAnimationFrame(() => {
          setEdges((eds) => {
            const withPromptEdge = addEdge(promptEdge, eds);
            return addEdge(imageEdge, withPromptEdge);
          });
        });
      } else if (nodeType === 'sora2Video' || nodeType === 'veo3Video' || nodeType === 'doubaoVideo' || nodeType === 'hailuo23Video' || nodeType === 'klingVideo') {
        // 如果是视频生成节点，自动创建文本输入节点和图片参考节点并连接
        const promptNodeId = nanoid();
        const promptNode = {
          id: promptNodeId,
          type: 'promptInput',
          position: {
            x: position.x - 450,
            y: position.y - 120,
          },
          data: { prompt: '' },
        };

        const imageRefNodeId = nanoid();
        const imageRefNode = {
          id: imageRefNodeId,
          type: 'imageReference',
          position: {
            x: position.x - 450,
            y: position.y + 160,
          },
          data: { imageUrl: '' },
        };

        const videoNodeId = nanoid();
        const videoNode = {
          id: videoNodeId,
          type: nodeType,
          position,
          data: {},
        };

        // 创建连接边：文本输入 -> 视频节点（连接到文本输入点）
        const promptEdge = {
          id: nanoid(),
          source: promptNodeId,
          target: videoNodeId,
          targetHandle: 'prompt',
          type: 'default',
        };
        
        // 创建连接边：图片参考 -> 视频节点（连接到图片输入点）
        const imageEdge = {
          id: nanoid(),
          source: imageRefNodeId,
          target: videoNodeId,
          targetHandle: 'image',
          type: 'default',
        };

        console.log('拖放创建视频节点及相关节点:', position, nodeType);
        // 使用受控状态添加节点，避免与内部 store 的竞态
        setNodes((nds) => [...nds, promptNode, imageRefNode, videoNode]);
        
        // 使用微队列延迟添加边，确保节点完全渲染后再添加边
        requestAnimationFrame(() => {
          setEdges((eds) => {
            const withPromptEdge = addEdge(promptEdge, eds);
            return addEdge(imageEdge, withPromptEdge);
          });
        });
      } else if (nodeType === 'videoFaceSwap') {
        // 如果是视频换脸节点，自动创建图片参考节点（人脸）和视频参考节点（源视频）
        const faceNodeId = nanoid();
        const faceNode = {
          id: faceNodeId,
          type: 'imageReference',
          position: {
            x: position.x - 450,
            y: position.y - 120,
          },
          data: { imageUrl: '' },
        };

        const videoRefNodeId = nanoid();
        const videoRefNode = {
          id: videoRefNodeId,
          type: 'videoReference',
          position: {
            x: position.x - 450,
            y: position.y + 160,
          },
          data: { videoUrl: '' },
        };

        const faceSwapNodeId = nanoid();
        const faceSwapNode = {
          id: faceSwapNodeId,
          type: nodeType,
          position,
          data: {},
        };

        // 创建连接边：图片参考 -> 视频换脸节点（连接到人脸输入点）
        const faceEdge = {
          id: nanoid(),
          source: faceNodeId,
          target: faceSwapNodeId,
          targetHandle: 'face',
          type: 'default',
        };
        
        // 创建连接边：视频参考 -> 视频换脸节点（连接到视频输入点）
        const videoEdge = {
          id: nanoid(),
          source: videoRefNodeId,
          target: faceSwapNodeId,
          targetHandle: 'video',
          type: 'default',
        };

        console.log('拖放创建视频换脸节点及相关节点:', position);
        // 使用受控状态添加节点，避免与内部 store 的竞态
        setNodes((nds) => [...nds, faceNode, videoRefNode, faceSwapNode]);
        
        // 使用微队列延迟添加边，确保节点完全渲染后再添加边
        requestAnimationFrame(() => {
          setEdges((eds) => {
            const withFaceEdge = addEdge(faceEdge, eds);
            return addEdge(videoEdge, withFaceEdge);
          });
        });
      } else if (nodeType === 'klingLipSync') {
        // 如果是可灵对口型节点，自动创建视频参考节点和音频参考节点
        const videoRefNodeId = nanoid();
        const videoRefNode = {
          id: videoRefNodeId,
          type: 'videoReference',
          position: {
            x: position.x - 450,
            y: position.y - 120,
          },
          data: { videoUrl: '' },
        };

        const audioRefNodeId = nanoid();
        const audioRefNode = {
          id: audioRefNodeId,
          type: 'audioReference',
          position: {
            x: position.x - 450,
            y: position.y + 120,
          },
          data: { audioUrl: '' },
        };

        const lipSyncNodeId = nanoid();
        const lipSyncNode = {
          id: lipSyncNodeId,
          type: nodeType,
          position,
          data: {},
        };

        // 创建连接边：视频参考 -> 可灵对口型节点（连接到视频输入点）
        const videoEdge = {
          id: nanoid(),
          source: videoRefNodeId,
          target: lipSyncNodeId,
          targetHandle: 'video',
          type: 'default',
        };
        
        // 创建连接边：音频参考 -> 可灵对口型节点（连接到音频输入点）
        const audioEdge = {
          id: nanoid(),
          source: audioRefNodeId,
          target: lipSyncNodeId,
          targetHandle: 'audio',
          type: 'default',
        };

        console.log('拖放创建可灵对口型节点及相关节点:', position);
        // 使用受控状态添加节点，避免与内部 store 的竞态
        setNodes((nds) => [...nds, videoRefNode, audioRefNode, lipSyncNode]);
        
        // 使用微队列延迟添加边，确保节点完全渲染后再添加边
        requestAnimationFrame(() => {
          setEdges((eds) => {
            const withVideoEdge = addEdge(videoEdge, eds);
            return addEdge(audioEdge, withVideoEdge);
          });
        });
      } else if (nodeType === 'textToAudio') {
        // 如果是语音合成节点，自动创建文本输入节点并连接
        const promptNodeId = nanoid();
        const promptNode = {
          id: promptNodeId,
          type: 'promptInput',
          position: {
            x: position.x - 450, // 在左侧450px处
            y: position.y,
          },
          data: { prompt: '' },
        };

        const textToAudioNodeId = nanoid();
        const textToAudioNode = {
          id: textToAudioNodeId,
          type: nodeType,
          position,
          data: {},
        };

        // 创建连接边：文本输入 -> 语音合成节点（连接到文本输入点）
        const promptEdge = {
          id: nanoid(),
          source: promptNodeId,
          target: textToAudioNodeId,
          targetHandle: 'prompt',
          type: 'default',
        };

        console.log('拖放创建语音合成节点及文本输入节点:', position);
        // 使用受控状态添加节点，避免与内部 store 的竞态
        setNodes((nds) => [...nds, promptNode, textToAudioNode]);
        
        // 使用微队列延迟添加边，确保节点完全渲染后再添加边
        requestAnimationFrame(() => {
          setEdges((eds) => addEdge(promptEdge, eds));
        });
      } else if (nodeType === 'hailuoMusic') {
        // 如果是音乐生成节点，自动创建两个文本输入节点并连接
        // 第一个文本输入节点：音乐描述
        const promptNodeId = nanoid();
        const promptNode = {
          id: promptNodeId,
          type: 'promptInput',
          position: {
            x: position.x - 450, // 在左侧450px处
            y: position.y - 80,   // 在上方
          },
          data: { prompt: '' },
        };

        // 第二个文本输入节点：歌词
        const lyricsNodeId = nanoid();
        const lyricsNode = {
          id: lyricsNodeId,
          type: 'promptInput',
          position: {
            x: position.x - 450, // 在左侧450px处
            y: position.y + 80,   // 在下方
          },
          data: { prompt: '' },
        };

        const musicNodeId = nanoid();
        const musicNode = {
          id: musicNodeId,
          type: nodeType,
          position,
          data: {},
        };

        // 创建连接边1：音乐描述文本输入 -> 音乐生成节点（上方Handle）
        const promptEdge = {
          id: nanoid(),
          source: promptNodeId,
          target: musicNodeId,
          targetHandle: 'prompt',
          type: 'default',
        };

        // 创建连接边2：歌词文本输入 -> 音乐生成节点（下方Handle）
        const lyricsEdge = {
          id: nanoid(),
          source: lyricsNodeId,
          target: musicNodeId,
          targetHandle: 'lyrics',
          type: 'default',
        };

        console.log('拖放创建音乐生成节点及两个文本输入节点:', position);
        // 使用受控状态添加节点，避免与内部 store 的竞态
        setNodes((nds) => [...nds, promptNode, lyricsNode, musicNode]);
        
        // 使用微队列延迟添加边，确保节点完全渲染后再添加边
        requestAnimationFrame(() => {
          setEdges((eds) => {
            const withPromptEdge = addEdge(promptEdge, eds);
            return addEdge(lyricsEdge, withPromptEdge);
          });
        });
      } else if (nodeType === 'sunoMusic') {
        // 如果是 Suno 音乐生成节点，自动创建一个文本输入节点（歌词）并连接
        const lyricsNodeId = nanoid();
        const lyricsNode = {
          id: lyricsNodeId,
          type: 'promptInput',
          position: {
            x: position.x - 450, // 在左侧450px处
            y: position.y,
          },
          data: { prompt: '' },
        };

        const sunoNodeId = nanoid();
        const sunoNode = {
          id: sunoNodeId,
          type: nodeType,
          position,
          data: {},
        };

        // 创建连接边：歌词文本输入 -> Suno音乐生成节点
        const lyricsEdge = {
          id: nanoid(),
          source: lyricsNodeId,
          target: sunoNodeId,
          targetHandle: 'lyrics',
          type: 'default',
        };

        console.log('拖放创建Suno音乐生成节点及文本输入节点:', position);
        // 使用受控状态添加节点
        setNodes((nds) => [...nds, lyricsNode, sunoNode]);
        
        // 使用微队列延迟添加边，确保节点完全渲染后再添加边
        requestAnimationFrame(() => {
          setEdges((eds) => addEdge(lyricsEdge, eds));
        });
      } else if (nodeType === 'bananaPro') {
        // 如果是 Banana Pro 节点，自动创建一个文本输入节点并连接
        const promptNodeId = nanoid();
        const promptNode = {
          id: promptNodeId,
          type: 'promptInput',
          position: {
            x: position.x - 450,
            y: position.y,
          },
          data: { prompt: '' },
        };

        const bananaProNodeId = nanoid();
        const bananaProNode = {
          id: bananaProNodeId,
          type: nodeType,
          position,
          data: {},
        };

        // 创建连接边：文本输入 -> Banana Pro 节点
        const promptEdge = {
          id: nanoid(),
          source: promptNodeId,
          target: bananaProNodeId,
          targetHandle: 'prompt',
          type: 'default',
        };

        console.log('拖放创建 Banana Pro 节点及文本输入节点:', position);
        setNodes((nds) => [...nds, promptNode, bananaProNode]);
        
        requestAnimationFrame(() => {
          setEdges((eds) => addEdge(promptEdge, eds));
        });
      } else {
        // 其他节点正常创建
        const newNode = {
          id: nanoid(),
          type: nodeType,
          position,
          data: {},
        };

        console.log('拖放创建节点:', nodeType, '位置:', position);
        setNodes((nds) => [...nds, newNode]);
      }
    },
    [screenToFlowPosition, readOnly]
  );

  return (
    <NodeDropzoneProvider>
      <div className="Canvas-wrapper reactflow-wrapper">{/* 添加 reactflow-wrapper 供引导定位 */}
        <ReactFlow
          nodes={nodes}
          edges={displayEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDoubleClick={handleDoubleClick}
          onDrop={onDrop}
          onDragOver={onDragOver}
          isValidConnection={isValidConnection}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={true}
          panOnScroll
          panOnDrag={panMode}
          selectionOnDrag={!panMode}
          selectionMode={!panMode ? 'partial' : undefined}
          fitView
          fitViewOptions={{
            padding: 0.2,
            maxZoom: 2,
            minZoom: 0.1,
          }}
          minZoom={0.1}
          maxZoom={2}
          zoomOnDoubleClick={false}
          className="Canvas-reactflow"
        >
          <Background color="#ddd" gap={16} />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              switch (node.type) {
                case 'promptInput':
                  return '#667eea';
                case 'imageReference':
                  return '#48bb78';
                case 'drawing':
                  return '#ff6b6b';
                case 'textToImage':
                  return '#3b82f6';
                case 'referenceToImage':
                  return '#10b981';
                case 'imengImageGen':
                  return '#6366f1';
                case 'gptImage2':
                  return '#1463ff';
                case 'nanoBanana2':
                  return '#16794d';
                case 'minimaxH3Video':
                  return '#df4b2f';
                case 'klingO3ReferenceVideo':
                  return '#2876d7';
                case 'geminiOmniFlashVideo':
                  return '#6b4ad8';
                case 'superSeed2LiteVideo':
                  return '#13835e';
                case 'imageTask':
                  return '#f59e0b';
                case 'imageDisplay':
                  return '#9333ea';
                case 'sora2Video':
                  return '#8b5cf6';
                case 'veo3Video':
                  return '#10b981';
                case 'doubaoVideo':
                  return '#f59e0b';
                case 'videoTask':
                  return '#ec4899';
                case 'klingLipSync':
                  return '#ec4899';
                default:
                  return '#ccc';
              }
            }}
          />
        </ReactFlow>
        {/* 只读模式下不显示工具栏 */}
        {!readOnly && <Toolbar />}
        
        {/* 保存状态指示器 */}
        {/* {saveStatus && (
          <div className={`Canvas-save-indicator Canvas-save-indicator-${saveStatus}`}>
            {saveStatus === 'saving' ? (
              <>
                <span className="Canvas-save-spinner"></span>
                正在保存...
              </>
            ) : (
              <>
                <span className="Canvas-save-check">✓</span>
                已保存
              </>
            )}
          </div>
        )} */}
        
        {/* 模式切换按钮 */}
        {!readOnly && (
          <div className="Canvas-mode-toggle">
            <button
              className={`Canvas-mode-button ${panMode ? 'Canvas-mode-button-active' : ''}`}
              onClick={() => setPanMode(true)}
              title="抓取模式 - 拖动画布"
            >
              <span className="Canvas-mode-icon"><i className="bi bi-hand-index"></i></span>
              <span className="Canvas-mode-label">抓取</span>
            </button>
            <button
              className={`Canvas-mode-button ${!panMode ? 'Canvas-mode-button-active' : ''}`}
              onClick={() => setPanMode(false)}
              title="选择模式 - 框选节点"
            >
              <span className="Canvas-mode-icon Canvas-mode-icon-flip"><i className="bi bi-cursor-fill"></i></span>
              <span className="Canvas-mode-label">选择</span>
            </button>
          </div>
        )}
      </div>
    </NodeDropzoneProvider>
  );
};

export const Canvas = (props) => {
  return (
    <ReactFlowProvider>
      <NodeOperationsProvider>
        <CanvasInner {...props} />
      </NodeOperationsProvider>
    </ReactFlowProvider>
  );
};
